/**
 * @fileoverview The one thing Grow must ask before the garden can be shown: where it is.
 *
 * On Today, in the place of To do, under "Where is your garden, Rosa?", for
 * any account without a postcode. Nothing else can be worked out without it:
 * the forecast, the frost dates, every planting window, every plant's week.
 * Until it is saved the other tabs are hidden (Header, TabBar), and so is the
 * trial line, so Today is the one place to give it; a page opened by its
 * address says so and links here.
 *
 * Sign-up asks only for a name now, so this is where every new garden gives
 * its postcode. It used to be asked there, and by a screen over everything
 * (WelcomeScreen), with nothing of Grow behind either. Saving it is when the
 * welcome email goes, in the gardener's language (the backend's PUT).
 *
 * Its words are a tip (the `postcode` tip at place `today_postcode`, a rule
 * on the Grow settings panel's Today tab, Tips): the title is Today's heading, the line and the button
 * are here. Without the tip, the app's own strings say the same: "Your ZIP
 * code gives Grow your forecast and your frost dates." and "See my garden's
 * weather".
 *
 * Checked as it is typed: once it is a ZIP's shape, the backend says whether
 * it is a real place Grow can plan for, and the place shows under the box
 * ("✓ Sparta, TN") or why not; a tap before the check is back is kept and saves the moment it says yes (L1, 2026-10-21: the first tap used to do nothing). The save checks
 * again, and sets the coordinates (the weather station and forecast grid are
 * found from them) from the ZIP table, so no browser geocoder is asked.
 *
 * A code that is not a US ZIP (letters in it, or the wrong number of digits
 * once the box is left) is answered plainly, "Grow works with US ZIP codes for
 * now.", with an offer: "Leave your email and we'll tell you when Grow reaches
 * you." The email starts as the account's; the backend keeps it with the
 * country (askToBeTold). It used to say "Five digits, like 38583" for ever.
 *
 * A postcode saved before these checks that cannot be used (a UK or
 * Canadian code, a ZIP that is not real) is shown with why (problem), and
 * this box asks for a new one. Whether the garden's postcode can be used is
 * the server's answer (the garden place, useGardenPostcode). In someone
 * else's garden (canGive false) there is no box: the garden's postcode is
 * its owner's to give, so a member is told so (problem) and nothing they
 * type is saved to their own profile (audit A29).
 *
 * Where: Today's first step, in the place of To do, for a garden without a
 * usable postcode.
 * API: GET /postcodes/:code (checkPostcode, as it is typed); PUT
 * /users/:id { postal_code } (updateUserProfile; the backend also sets the
 * coordinates and sends the welcome email); POST /postcodes/notify
 * (askToBeTold, the waitlist for a code outside the US).
 * Settings: the postcode tip's words (Grow settings, Tips); TODAY_QUERY_RETRIES.
 * Words (en/es): welcome.zip.* (body, label, checking, start, saving,
 * notUsShort, tellMe.*), auth.emailAddress, sitewide.error.
 * A failure on our side (a 500, no connection; D31, lib/serverFailure) says
 * the standard sentence, reported to Sentry, with the support address as a
 * link: in place of sitewide.error when saving, of tellMe.failed's "Try
 * again" on the waitlist, and under the box when the ZIP could not be
 * checked (which otherwise left the button waiting with nothing said).
 *
 * @module TodayPostcode
 */

import { retryRule } from '@/config/queryRetry';
import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { isUsablePostcode } from '@/shared/lib/postcode';
import { updateUserProfile } from '@/shared/api/users';
import { askToBeTold, checkPostcode, placeOf, postcodeCheckKey, postcodeRefusal } from '@/shared/api/postcodes';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import { TODAY_QUERY_RETRIES } from '@/config/tunables/today';

/** The tip's line and button, when there is one. */
interface TodayPostcodeProps {
  /** The line above the box, or empty for the app's own. */
  body?: string;
  /** The button's words, or empty for the app's own. */
  button?: string;
  /** Why the postcode on file cannot be used, when there is one that cannot. */
  problem?: string | null;
  /** False in someone else's garden: only its owner can give it a postcode, so the line is shown without the box. */
  canGive?: boolean;
}

/**
 * Whether what is typed is plainly not a US ZIP: it has letters in it (a UK
 * or Canadian postcode), or, once the box has been left, it is not five
 * digits (or a ZIP+4). While digits are still being typed it is not judged.
 *
 * @param typed - The box's text.
 * @param left - Whether the gardener has moved on from the box.
 * @returns True when the "Grow works with US ZIP codes for now" offer should show.
 */
function looksForeign(typed: string, left: boolean): boolean {
  const text = typed.trim();
  if (!text || isUsablePostcode(text)) return false;
  if (/[a-z]/i.test(text)) return true;
  return left;
}

/**
 * The browser's region from its language ("GB" from en-GB), for the country a postcode is in.
 *
 * @returns Two capital letters, or null when the language names no region.
 */
function browserRegion(): string | null {
  const tag = typeof navigator === 'undefined' ? '' : navigator.language ?? '';
  const region = tag.split(/[-_]/)[1] ?? '';
  return /^[a-z]{2}$/i.test(region) ? region.toUpperCase() : null;
}

/**
 * "Leave your email and we'll tell you when Grow reaches you": the email (the
 * account's to start with) and a button, then thanks.
 *
 * @param props.postcode - The code typed, sent with the email.
 */
const TellMeWhen: React.FC<{ postcode: string }> = ({ postcode }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const [email, setEmail] = React.useState(mysqlUser?.email ?? '');
  const [state, setState] = React.useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [sentTo, setSentTo] = React.useState('');
  // Why it failed, when it did (lib/errorMessage's sentence).
  const [failure, setFailure] = React.useState('');

  /** Sends the email and the postcode to the list. */
  const send = async (): Promise<void> => {
    const to = email.trim();
    if (!to) return;
    setState('sending');
    try {
      await askToBeTold({ email: to, postcode: postcode.trim(), region: browserRegion(), language });
      setSentTo(to);
      setState('sent');
    } catch (err) {
      debugLog('TodayPostcode', 'Could not join the waitlist', err);
      setFailure(errorMessage(err, t('welcome.zip.tellMe.failed')));
      setState('failed');
    }
  };

  if (state === 'sent') {
    return <p className="text-sm" role="status">{t('welcome.zip.tellMe.thanks', { email: sentTo })}</p>;
  }
  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('welcome.zip.tellMe.offer')}</p>
      <div className="flex gap-2">
        <label htmlFor="today-zip-email" className="sr-only">{t('auth.emailAddress')}</label>
        <Input
          id="today-zip-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === 'failed') setState('idle'); }}
          className="h-10 min-w-0 flex-1 text-base"
        />
        <Button type="button" variant="outline" className="h-10 shrink-0" disabled={!email.trim() || state === 'sending'} onClick={() => { void send(); }}>
          {t('welcome.zip.tellMe.button')}
        </Button>
      </div>
      {state === 'failed' && <p className="text-xs text-destructive"><FailureText text={failure} /></p>}
    </div>
  );
};

/**
 * The question, the box and the button. Saving fills Today in behind it.
 *
 * @param props - TodayPostcodeProps.
 */
export const TodayPostcode: React.FC<TodayPostcodeProps> = ({ body, button, problem = null, canGive = true }) => {
  const { reactTranslate: t } = useTranslation();
  const { mysqlUser, updateMysqlUser } = useAuth();
  const queryClient = useQueryClient();

  const [zip, setZip] = React.useState('');
  // Left the box: a short run of digits is then judged too, not only letters.
  const [left, setLeft] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const shaped = isUsablePostcode(zip);
  const foreign = looksForeign(zip, left);
  // The place, once the box holds a ZIP's shape: one answer per ZIP, kept.
  const { data: check, isFetching: checking, error: checkError } = useQuery({
    queryKey: postcodeCheckKey(zip),
    queryFn: () => checkPostcode(zip),
    enabled: shaped,
    staleTime: Infinity,
    retry: retryRule(TODAY_QUERY_RETRIES),
  });
  const ready = shaped && check?.ok === true;
  // A tap on the button made while the ZIP was still being checked: saved as soon as the check says yes.
  const [waiting, setWaiting] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  React.useEffect(() => {
    if (!waiting) return;
    if (ready) { setWaiting(false); formRef.current?.requestSubmit(); }
    // The check said no (or the ZIP changed): its note is on screen, and nothing is saved.
    else if (!shaped || (check && !check.ok) || checkError) setWaiting(false);
  }, [waiting, ready, shaped, check, checkError]);

  /**
   * Saves the postcode and where it is; every page keyed on it then reads the garden's place.
   *
   * @param event - The form's submit.
   */
  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!mysqlUser) return;
    // Tapped right after typing, before the ZIP's check is back (L1): the tap is kept and goes through the moment it is.
    if (!ready) { if (shaped && !check) setWaiting(true); return; }
    setSaving(true);
    setError(null);
    try {
      // The backend checks it again and sets the country and coordinates from the ZIP.
      const updated = await updateUserProfile(mysqlUser.user_id, { postal_code: zip.trim() });
      // The row as saved, merged into the account as it is now (not this render's copy).
      updateMysqlUser({ ...updated, postal_code: updated.postal_code ?? zip.trim() });
      // The garden was assembled for nowhere until now.
      // (The plants brief, with its planting dates, is kept by postcode: the new one is read afresh.)
      await queryClient.invalidateQueries({ queryKey: ['plants'] });
    } catch (err) {
      debugLog('TodayPostcode', 'Could not save postcode from Today', err);
      setError(postcodeRefusal(err, zip) ?? errorMessage(err, t('sitewide.error')));
    } finally {
      setSaving(false);
    }
  };

  // A member of someone else's garden: the owner gives the garden its postcode, so there is nothing to type here.
  if (!canGive) {
    return (
      <p className="rounded-md border border-warning/40 bg-warning-tint px-3 py-2 text-sm text-warning">
        {problem}
      </p>
    );
  }

  return (
    <form ref={formRef} onSubmit={(e) => { void save(e); }} className="space-y-3">
      {/* A postcode on file that cannot be used: why, before asking again. */}
      {problem && (
        <p className="rounded-md border border-warning/40 bg-warning-tint px-3 py-2 text-sm text-warning">
          {problem}
        </p>
      )}
      <p className="text-sm">{body || t('welcome.zip.body')}</p>
      <div className="space-y-1.5">
        <label htmlFor="today-zip" className="text-sm font-medium">{t('welcome.zip.label')}</label>
        <Input
          id="today-zip"
          value={zip}
          onChange={(e) => { setZip(e.target.value); setError(null); setLeft(false); }}
          onBlur={() => setLeft(true)}
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder={t('welcome.zip.placeholder')}
          className="h-11 max-w-[15rem] text-base"
        />
        {shaped && checking && !check && (
          <p className="text-xs text-muted-foreground">{t('welcome.zip.checking')}</p>
        )}
        {shaped && check?.ok && (
          <p className="flex items-center gap-1 text-sm font-medium text-primary">
            <Check className="h-4 w-4" aria-hidden="true" />{placeOf(check)}
          </p>
        )}
        {shaped && check && !check.ok && (
          <p className="text-xs text-warning">{check.message}</p>
        )}
        {/* The ZIP could not be checked, on our side: said, rather than a button that never wakes. */}
        {shaped && !check && !checking && isServerFailure(checkError) && (
          <ServerFailureNote error={checkError} where="Today postcode check" className="text-xs text-destructive" />
        )}
        {error && <p className="text-xs text-destructive"><FailureText text={error} /></p>}
      </div>
      {/* Not a US ZIP: said plainly, with a way to hear when that changes. */}
      {foreign ? (
        <div className="space-y-2 rounded-md border px-3 py-3">
          <p className="text-sm font-medium text-warning">{t('welcome.zip.notUsShort')}</p>
          <TellMeWhen postcode={zip} />
        </div>
      ) : (
        <Button type="submit" className="w-full" size="lg" disabled={!shaped || saving || (check?.ok === false)}>
          {saving || waiting ? t('welcome.zip.saving') : button || t('welcome.zip.start')}
        </Button>
      )}
    </form>
  );
};
